<template>
  <Modal
    :title="title"
    :saving="saving"
    :disabled="disabled"
    @submit="handleSubmit"
    @hide-modal="$emit('hide-modal')"
  >
    <div v-if="call">
      <div class="call-details">
        <span class="mr-1 font-bold">Caller ID:</span>
        {{ call.caller.name }} {{ call.caller.phone_number }}
        <div v-if="call.lead" class="d-flex">
          <divider vertical></divider>
          <span class="mr-1 font-bold">Call Source:</span>
          {{ call.lead.tracking_name }}
        </div>
      </div>
      <div class="d-flex mb-3">
        <div class="flex-1">
          <div class="font-bold">Status</div>
          <div>{{ callStatus }}</div>
        </div>
        <div class="flex-1">
          <div class="font-bold">Assigned To</div>
          <Transition>
            <div v-if="editing" class="d-flex align-items-center">
              <Select
                v-model="formData.admin_id"
                :options="adminOptions"
                placeholder="Unassigned"
              ></Select>
              <tooltip>
                <template #trigger
                  ><i
                    class="fa-solid fa-circle-xmark ml-2 text-gray"
                    @click="toggleEdit"
                  ></i
                ></template>
                <template #tooltip>Cancel Edit</template>
              </tooltip>
            </div>
            <div v-else class="d-flex align-items-center">
              <div>{{ call.claimed_by ? call.claimed_by.name : "--" }}</div>
              <i
                class="fa-solid fa-pen-to-square ml-1 link"
                @click="toggleEdit"
              ></i>
            </div>
          </Transition>
        </div>
      </div>
      <div class="d-flex mb-3">
        <div class="flex-1">
          <div class="font-bold">Start Time</div>
          <div>{{ datetime(call.started_at) }}</div>
        </div>
        <div class="flex-1">
          <div class="font-bold">Answered</div>
          <div>{{ call.answered ? "Yes" : "No" }}</div>
        </div>
        <div class="flex-1">
          <div class="font-bold">Duration</div>
          <div><seconds-label :seconds="call.duration"></seconds-label></div>
        </div>
        <div class="flex-1">
          <div class="font-bold">End Time</div>
          <div>{{ datetime(call.ended_at) }}</div>
        </div>
      </div>
      <div v-if="playable" class="d-flex mb-3" data-cy="call-details-route">
        <div class="flex-1">
          <div class="font-bold">Provider</div>
          <div>{{ providerLabel }}</div>
        </div>
        <div class="flex-1">
          <div class="font-bold">Direction</div>
          <div class="capitalize">{{ call.direction || "--" }}</div>
        </div>
        <div class="flex-1">
          <div class="font-bold">To</div>
          <div>{{ call.to_phone_number || "--" }}</div>
        </div>
      </div>
      <div class="font-bold">Linked To</div>
      <Table class="linked-table mb-3">
        <thead>
          <tr>
            <th>Date</th>
            <th>Customer</th>
            <th>Job #</th>
            <th class="text-center">Unlink</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="link in orderedLinkedCalls" :key="link.linkable_id">
            <td>{{ shortDate(link.linked_at) }}</td>
            <td>{{ link.customer_name }}</td>
            <td>
              <a
                v-if="link.linkable_type === 'Job'"
                :href="jobShowPath(link.linkable_id)"
                @click="$emit('hide-modal')"
                >{{ link.linkable_id }}</a
              >
              <span v-else>--</span>
            </td>
            <td class="text-center">
              <span class="link" @click="handleUnlinkCall(link)">
                <i class="fa-solid fa-link-slash"></i>
              </span>
            </td>
          </tr>
          <tr v-if="!call.linked_to.length">
            <td colspan="4" class="text-center">No matching records found</td>
          </tr>
        </tbody>
      </Table>
      <CallRecordings
        :call="call"
        :playable="playable"
        @playback-error="loadCall(call.id)"
      />
      <change-summary
        :changes="changes"
        :original="originalValues"
      ></change-summary>
    </div>
    <!-- Hide submit button if not editing -->
    <template v-if="!editing" #submit><div></div></template>
  </Modal>
</template>

<script>
import startCase from "lodash/startCase";
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getObjDiff, getProperty } from "@/lib/helpers.js";

import Divider from "@/components/Divider.vue";
import Modal from "@/components/Modal.vue";
import SecondsLabel from "@/components/SecondsLabel.vue";
import Table from "@/components/Table.vue";
import Tooltip from "@/components/Tooltip.vue";
import Transition from "@/components/Transition.vue";
import CONSTANTS from "@/Constants.js";
import ChangeSummary from "@/EditAppointmentModal/ChangeSummary.vue";
import { datetime, shortDate } from "@/mixins";
import { jobShowPath } from "@/mixins/path_helpers.js";
import CallRecordings from "./CallRecordings.vue";

const PROVIDER_LABELS = {
  call_rail: "CallRail",
  ring_central: "RingCentral",
  dialpad: "Dialpad"
};

export default {
  components: {
    Modal,
    ChangeSummary,
    Table,
    SecondsLabel,
    Transition,
    Divider,
    Tooltip,
    CallRecordings
  },
  props: {
    callId: {
      type: Number,
      default: null
    },
    // Plays recordings in the modal and shows the provider, direction and number called.
    playable: {
      type: Boolean,
      default: false
    }
  },
  emits: ["hide-modal"],
  data() {
    return {
      call: null,
      saving: false,
      editing: false,
      formData: {
        admin_id: null
      }
    };
  },
  computed: {
    ...mapState({
      admins: "admins",
      adminOptions({ admins }) {
        return admins.map((admin) => ({
          label: admin.full_name,
          value: admin.admin_id
        }));
      }
    }),
    orderedLinkedCalls() {
      if (!this.call?.linked_to) return [];
      return [...this.call.linked_to]
        .filter((l) => l.linked_at)
        .sort((a, b) => new Date(b.linked_at) - new Date(a.linked_at));
    },
    title() {
      if (!this.call) return "Call";
      return `Call ID ${this.call.id}`;
    },
    disabled() {
      return this.saving || Object.keys(this.changes).length === 0;
    },
    changes() {
      const assignedTo = this.admins.find(
        (admin) => admin.admin_id === this.formData.admin_id
      );
      return getObjDiff(this.originalValues, {
        assignee: assignedTo ? assignedTo.full_name : null
      });
    },
    originalValues() {
      const assignedToId = getProperty(this.call, "claimed_by.id") || null;
      const assignedTo = this.admins.find(
        (admin) => admin.admin_id === assignedToId
      );
      return {
        assignee: assignedTo ? assignedTo.full_name : null
      };
    },
    providerLabel() {
      const name = this.call?.source_name;
      return PROVIDER_LABELS[name] || (name ? startCase(name) : "Manual");
    },
    callStatus() {
      if (!this.call) return "--";
      return CONSTANTS.CALL_STATUS_MAP[this.call.status];
    }
  },
  watch: {
    editing(newValue) {
      // Reset form data after cancelling editing
      if (!newValue) {
        this.formData.admin_id = getProperty(this.call, "claimed_by.id");
      }
    }
  },
  mounted() {
    if (this.callId) {
      this.loadCall(this.callId);
    }
    if (!this.admins.length) {
      this.fetchAdminsForCall(this.callId);
    }
  },
  methods: {
    ...mapActions([
      "assignCall",
      "fetchCall",
      "confirmChanges",
      "unlinkCall",
      "fetchAdminsForCall"
    ]),
    jobShowPath,
    datetime,
    shortDate,
    handleSubmit() {
      this.saving = true;
      this.assignCall({
        link: this.call.links.assign_call,
        admin_id: this.formData.admin_id
      })
        .then((res) => {
          this.setFormData(res.data);
          window.notify.success("Call updated!");
          this.editing = false;
          EventBus.$emit("refresh-datatable");
        })
        .finally(() => (this.saving = false));
    },
    handleUnlinkCall(link) {
      this.confirmChanges({
        title: "Unlink Phone Call",
        message:
          "Are you sure you want to unlink this phone call from the source? This action cannot be undone."
      })
        .then(() => {
          this.unlinkCall({
            link: link.unlink_call,
            call_id: this.call.id
          }).then(() => {
            window.notify.success("Call updated!");
            this.loadCall(this.call.id);
            EventBus.$emit("refresh-datatable");
          });
        })
        .catch(() => {});
    },
    toggleEdit() {
      this.editing = !this.editing;
    },
    setFormData(data) {
      this.call = data;
      this.formData.admin_id = getProperty(data, "claimed_by.id");
    },
    loadCall(callId) {
      this.fetchCall(callId).then((res) => this.setFormData(res.data));
    }
  }
};
</script>

<style lang="scss" scoped>
.linked-table {
  box-shadow: var(--box-shadow);
}
.call-details {
  margin-bottom: 15px;
  display: flex;
}
</style>
